import {
  combineSlices,
  configureStore,
  createDynamicMiddleware,
  isRejected,
  Middleware,
  UnknownAction,
} from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import { treatmentApi } from '../treatmentScreen/treatment.api'
import { appConfigApi } from '../appConfig/appConfig.api'
import { clinicApi } from '../clinicScreen/clinic.api'
import { cityScreenApi } from '../cityScreen/cityScreen.api'
import { appointmentScreenApi } from '../appointmentScreen/appointmentScreen.api'
import { setupListeners } from '@reduxjs/toolkit/query'
import type { LazyLoadedSlices, RootState } from './types'
import { promoCodeApi } from '../promoCode/promoCode.api'
import { bookingFlowApi } from '../bookingFlow/bookingFlow.api'
import { yourBookingsApi } from '../yourBookings/yourBookingsSlice.api'

export const rootReducer = combineSlices(
  treatmentApi,
  appConfigApi,
  clinicApi,
  cityScreenApi,
  appointmentScreenApi,
  promoCodeApi,
  bookingFlowApi,
  yourBookingsApi
).withLazyLoadedSlices<LazyLoadedSlices>()

const errorMiddleware: Middleware = () => (next) => (action) => {
  const result = next(action)

  if (isRejected(result)) {
    const args = result.meta?.arg
    if (!result.meta?.rejectedWithValue) {
      const error = new Error(result.error?.message || 'Something went wrong')
      if (result.error.name) {
        error.name = result.error.name
      }
      if (result.error.stack) {
        error.stack = result.error.stack
      }

      Sentry.captureException(error.message, {
        extra: {
          message: error.message,
          type: (action as UnknownAction).type,
          args,
        },
      })
    }
    return result
  }

  return result
}

export const dynamicMiddleware = createDynamicMiddleware()

export const setupStore = (preloadedState?: Partial<RootState>) => {
  const store = configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        treatmentApi.middleware,
        appConfigApi.middleware,
        clinicApi.middleware,
        cityScreenApi.middleware,
        appointmentScreenApi.middleware,
        promoCodeApi.middleware,
        bookingFlowApi.middleware,
        yourBookingsApi.middleware,
        errorMiddleware,
        dynamicMiddleware.middleware
      ),
  })

  // configure listeners using the provided defaults
  // optional, but required for `refetchOnFocus`/`refetchOnReconnect` behaviors
  setupListeners(store.dispatch)
  return store
}

export const store = setupStore()
